import {
  CellClassParams,
  CellStyle,
  ColDef,
  ColumnApi,
  GridOptions,
  GridReadyEvent,
  ITextFilterParams,
  Module,
  NumberFilter,
  ProcessCellForExportParams,
  RowDoubleClickedEvent,
  RowNode,
  SelectionChangedEvent,
  TextFilter,
  ValueFormatterParams,
  ValueGetterParams,
} from '@ag-grid-community/core'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  didHandleRowGroupDoubleClick,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  generateHeliosAgGridSidebarFromScratch,
  generateMdiIcon,
  heliosClientSideRowConfig,
  heliosDefaultColWidth,
  heliosDefaultToolPanels,
  heliosMultiRowSelectionConfig,
  HsBreakStatusColDef,
  IssueStatusName,
  TextFilterOptionsWithContainsIn,
} from '@helios/ag-grid-config'
import {
  COMMENTARY_COLUMN_ID,
  CommentaryCountsService,
  CommentaryLiveAccountsService,
  CommentaryEntityRef,
  CommentaryEntitySearchService,
  CommentaryFollowService,
  CommentaryFollowTarget,
  CommentaryGridSelectionService,
  CommentCountCellRenderer,
  commentCountRendererKey,
  followNotice,
  FollowNoticeReason,
  getCommentaryColumnIfAccessible,
  getCommentaryContextMenuItems,
  wireCommentaryCounts,
  refilterOnCommentCountChanges,
  wireCommentaryGrid,
  breakCounts,
} from '@helios/comments'
import {
  AccountClient,
  formatDates,
  getUltClientData,
} from '@helios/common-api'
import { FilterModalService } from '@helios/filter-modal'
import {
  HighlightingService,
  mergeBaseColDefsWithColumnState,
} from '@helios/generic-components'
import {
  HotkeyDestroyFn,
  HotkeysService,
  MultiBreaksPanelHotkeysType,
} from '@helios/hotkeys'
import { countFilters } from '@helios/server-side-util'
import {
  BreakCategory,
  GridState,
  RootState,
  selectActiveSavedViewForGrid,
  selectActiveSession,
  selectAllSessionCacheStatus,
  selectFirstFocusedAccountInfo,
  selectSavedViewExistsForGridKey,
  selectUserPolicy,
  SessionCacheName,
  SessionCacheStatus,
  userHasCommentaryAccess,
} from '@helios/state-management'
import {
  createFilterGroupsToolPanel,
  DensityLevel,
  FilterGroupsToolPanelParams,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
  WorkspaceUpdateEventObject,
} from '@helios/user-preferences'
import { formatTooltip, isNullOrEmpty } from '@helios/utils'
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi, NfColumnTypes } from '@northfork/ag-grid'
import { AgGridState } from '@northfork/ag-grid-angular'
import { composeFieldPath } from '@northfork/basics'
import { ToastService } from '@northfork/ng-basics'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { createPanelSettingsToolPanel } from 'libs/generic-components/src/lib/panel-settings-tool-panel/utils/panel-settings-tool-panel-sidebar-utils'
import { Observable, of, Subject } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  takeWhile,
} from 'rxjs/operators'
import { MultiBreaksKey } from '../../../panel-key'
import {
  Break,
  BreakAvailableRestrictedColumns,
  FindBreaksForm,
  UserFeatureAccess,
} from '../../model/break.model'
import {
  BREAK_CODES_COPIED,
  BreakCodeClipboardService,
} from '../../services/break-code-clipboard.service'
import { MultiBreaksContextMenuService } from '../../services/multi-breaks-context-menu.service'
import { MultiBreaksService } from '../../services/multi-breaks.service'
import { formatMetaDataTooltip } from '../../templates/metadata-tooltip'
import {
  breakColumnTypes,
  HsBreakPriorityColDef,
} from '../../utils/break-priority-column'
import { getGroupCategoryPriority } from '../../utils/group-category'
import {
  combinedMetaDataValueGetter,
  differenceValueGetter,
  formatNumber,
  generateMetaDataColumn,
  generateMetaDataColumnForNumber,
  issueMetaDataValueGetter,
} from '../../utils/metadata-columns'
import { formatBreakNote } from '../../utils/note-formatter'
import { breakCommentary } from './multi-breaks-commentary'
import {
  MultiBreaksStatusBarModule,
  multiBreaksStatusBarPanel,
} from './multi-breaks-status-bar.module'

export const BreaksMainGridKey = `${MultiBreaksKey}-MainGrid`
const field = composeFieldPath([] as Break[])

/**
 * colId of the UI-only "Break Codes" column (PVAL-26115). The column has no
 * backing `field`: its value is composed by a valueGetter, so it needs an
 * explicit, stable colId for persisted column state to key on.
 */
export const BREAK_CODES_COL_ID = 'breakCodes'

/**
 * Restricted-column keys whose content the "Break Codes" column re-renders.
 *
 * `breakCodes` composes `<descriptionCode> | <actionCode> | <note>`, so it is a
 * DERIVED column: when an entitlement makes any one of these source columns
 * unavailable, hiding only the source column still leaks its value through
 * Break Codes. Both restriction paths therefore expand the hide list via
 * {@link withDerivedRestrictedColumns}.
 */
export const BREAK_CODES_SOURCE_COL_IDS = [
  'descriptionCode',
  'actionCode',
  'note',
]

/**
 * Expands a list of restricted colIds to hide with the derived columns that
 * depend on them. Preserves input order and never emits a duplicate.
 *
 * @internal
 */
export function withDerivedRestrictedColumns(
  columnsToHide: string[],
): string[] {
  const hidesBreakCodesSource = BREAK_CODES_SOURCE_COL_IDS.some(colId =>
    columnsToHide.includes(colId),
  )
  if (!hidesBreakCodesSource || columnsToHide.includes(BREAK_CODES_COL_ID)) {
    return columnsToHide
  }
  return [...columnsToHide, BREAK_CODES_COL_ID]
}

/**
 * Single token every blank value collapses into for header-filter purposes
 * (PVAL-26384). Matches the label the Security ID column already uses, so the
 * set filter offers exactly ONE blank entry instead of one indistinguishable
 * entry per flavour of blank (`null`, `undefined`, `''`).
 */
export const BLANKS_FILTER_VALUE = '(Blanks)'

/**
 * Filter-only projection that maps every flavour of blank onto
 * {@link BLANKS_FILTER_VALUE} and passes any real value through untouched
 * (PVAL-26384).
 *
 * Used from `filterValueGetter` ONLY — the cell `valueGetter` / `valueFormatter`
 * are deliberately left alone so rendered cells and the Excel export payload
 * are unchanged.
 *
 * @internal
 */
export function blanksCollapsingFilterValue(value: unknown): unknown {
  return isNullOrEmpty(value as string | null) ? BLANKS_FILTER_VALUE : value
}

const OPEN_BREAK_STATUSES: readonly string[] = [
  IssueStatusName.FOUND,
  IssueStatusName.WAITING,
  IssueStatusName.IGNORED,
]

@Component({
  selector: 'hs-multi-breaks-grid',
  templateUrl: './multi-breaks-grid.component.html',
  styleUrls: ['./multi-breaks-grid.component.scss'],
  providers: [
    FilterModalService,
    MultiBreaksContextMenuService,
    HighlightingService,
  ],
})
export class MultiBreaksGridComponent implements OnInit, OnDestroy {
  private readonly destroySubject$ = new Subject<void>()
  private _breaksSelection: Break[] = []
  filterCount = 0
  popOverConfigGrid: FindBreaksForm = {
    rootType: 'accountIds',
    rootIds: [],
    searchIdType: 'assetIds',
    searchByIds: [],
    breakStatus: ['FOUND'],
    breakPriority: [],
    breakTypes: [],
    breakIds: [],
    breakCategories: [],
  }
  data: Break[] | null = []
  @Input() set dataVal(data: Break[] | null) {
    this.data = data
  }
  @Input() isLoading = false
  @Input() isDownloadSubmitted: Observable<boolean> = of(false)
  @Input() totalFocusedBreaks?: number | null = 0
  @Input() set popOverConfig(popConfig: FindBreaksForm | undefined) {
    this.setPopOverOptions(popConfig)
    if (popConfig) this.updateGridConfig()
  }
  @Input() breaksLimitExceeded: boolean | null = false
  @Input() invalidPermissions = false
  @Input() hasActiveSession: boolean | null = false
  @Input() isInCurrentSession: boolean | null = false
  @Input() isJobCompleted = false
  @Input() hasSameUltimateParent: boolean | null = false
  @Input() hasAccountId: boolean | null = false
  @Input() currentSessionAccountIds: number[] = []
  @Input() updatedBreaks$: Subject<Break[]> = new Subject()
  @Input() ultClientData: AccountClient[] = []
  @Input() set userFeatureAccess(features: UserFeatureAccess | null) {
    if (features == null) {
      this.hasFindCurrentSessionFeature = false
      this.hasFindBreaksFeature = false
    } else {
      this.hasFindCurrentSessionFeature = features.findCurrentSessionBreaks
      this.hasFindBreaksFeature = features.findBreaks
    }
  }

  @Output() currentSessionSearchClicked = new EventEmitter<void>()
  @Output() showDetails = this.contextMenuService.showDetailsSelected
  @Output() editNote = this.contextMenuService.editNoteSelected
  @Output() bulkEdit = this.contextMenuService.bulkEditSelected
  @Output() accept = this.contextMenuService.acceptSelected
  @Output() hold = this.contextMenuService.holdSelected
  @Output() unaccept = this.contextMenuService.unacceptSelected
  @Output() createIssue = this.contextMenuService.createIssueSelected
  @Output() resolveCashBreak = this.contextMenuService.cashBreakSelected
  @Output() openCashBreakSettings =
    this.contextMenuService.cashBreakSettingsSelected
  @Output() generateEmail = this.contextMenuService.breakSelected
  @Output() refreshAfterJobCompleted = new EventEmitter<void>()
  @Output() resetPopOverFilter = new EventEmitter<void>()
  @Output() onLoadAccount = new EventEmitter<{
    accountIds: number[]
  }>()
  @Output() initialSearchFilters = new EventEmitter<FindBreaksForm>()
  @Output() updateUltClientData = this.contextMenuService.updateUltClientData
  @Output() exportToExcel = new EventEmitter<{ [key: string]: string }>()

  clearOnAccountLoad = new FormControl(true)
  gridOptions$ = this.createGridOptions()
  availableRestrictedColumns = this.getAvailableRestrictedColumns()

  /** @internal */
  gridApi!: AgGridApi
  gridModules: Module[] = [RangeSelectionModule, MultiBreaksStatusBarModule]
  clearFiltersEnabled = false
  MAX_BREAKS = 10000
  gridState?: GridState
  searchText = new FormControl('')
  hasFindCurrentSessionFeature = false
  hasFindBreaksFeature = false
  sessionSimpleAccountIds: number[] = []
  hasAccessCommentary = false

  private destroySubjectOnDoubleClick$ = new Subject<void>()
  private readonly hotkeyDestroyStack: HotkeyDestroyFn[] = []

  /**
   * @internal
   */
  get filterGroupsToolPanelParams(): FilterGroupsToolPanelParams<unknown> {
    return {
      gridKey: BreaksMainGridKey,
      getExternalFilters: () => ({}),
      setExternalFilters: () => undefined,
      cacheLoaded: of(true),
    }
  }

  /**
   * @internal
   */
  getDestroySubjectOnDoubleClick(): Subject<void> {
    return this.destroySubjectOnDoubleClick$
  }

  constructor(
    // @internal
    public filterModalService: FilterModalService,
    private readonly contextMenuService: MultiBreaksContextMenuService,
    private readonly breakCodeClipboard: BreakCodeClipboardService,
    private readonly toastService: ToastService,
    private readonly breaksService: MultiBreaksService,
    /** @internal */
    readonly highlightService: HighlightingService,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly hotkeysService: HotkeysService<MultiBreaksPanelHotkeysType>,
    private readonly store: Store<RootState>,
    private readonly commentaryCountsService: CommentaryCountsService,
    private readonly commentaryLiveAccounts: CommentaryLiveAccountsService,
    private readonly commentaryFollowService: CommentaryFollowService,
    private readonly entitySearchService: CommentaryEntitySearchService,
    private readonly gridSelectionService: CommentaryGridSelectionService,
  ) {}

  ngOnInit(): void {
    this.clearOnAccountLoad.valueChanges.subscribe(() => {
      this.onFilterClearingStatusChanged()
    })
    this.updatedBreaks$
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(value => this.processBreakChange(value))
    this.searchText.valueChanges
      .pipe(takeUntil(this.destroySubject$), debounceTime(500))
      .subscribe(() => {
        this.gridApi?.api.onFilterChanged()
      })
    this.store
      .select(selectFirstFocusedAccountInfo)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(accountInfo => {
        this.sessionSimpleAccountIds = accountInfo?.simpleAccountIds ?? []
      })

    this.store
      .select(selectUserPolicy)
      .pipe(
        takeUntil(this.destroySubject$),
        map(policy => userHasCommentaryAccess(policy)),
        // keep listening only until access is first granted, then complete
        takeWhile(access => !access, true),
      )
      .subscribe(access => {
        this.hasAccessCommentary = access
        if (access) {
          this.wireCommentary()
        }
      })
    this.registerHotkeys()
  }

  /**
   * DLITE-3849 — wire this grid into the commentary drawer (follow-a-chip row
   * focus + entitySearch/pickEntities sources). multi-breaks owns `break`;
   * idColId is `identifier` (the break entity id equals the row-node id).
   * Called at most once — the policy pipe in ngOnInit completes after its
   * first granting emission. Torn down via destroySubject$.
   */
  private wireCommentary() {
    wireCommentaryGrid({
      follow: this.commentaryFollowService,
      search: this.entitySearchService,
      selection: this.gridSelectionService,
      panelKey: MultiBreaksKey,
      entityType: 'break',
      idColId: 'identifier',
      getApi: () => this.gridApi?.api,
      hasAccess: () => this.hasAccessCommentary,
      destroy$: this.destroySubject$,
      loadedRefs: () => this.collectLoadedBreakRefs(),
      selectedRefs: () => this.collectSelectedBreakRefs(),
      onMissing: target => void this.reportMissingBreak(target),
    })
  }

  /**
   * The grid holds only the user's last break search, so a break missing from
   * it may still be open; only the server can say it's gone.
   */
  private async reportMissingBreak(target: CommentaryFollowTarget) {
    let reason: FollowNoticeReason
    try {
      const response = await this.breaksService
        .getBreaks({
          rootType: 'breakIds',
          rootIds: [],
          searchIdType: 'assetIds',
          searchByIds: [],
          breakStatus: [],
          breakPriority: [],
          breakTypes: [],
          breakIds: [target.id],
          breakCategories: [],
        })
        .toPromise()
      const open = response?.data?.some(b =>
        OPEN_BREAK_STATUSES.includes(b.statusName),
      )
      reason = open ? 'breakOutsideSearch' : 'notFound'
    } catch {
      reason = 'breakNotLoaded'
    }
    const { title, message } = followNotice(target, reason)
    this.toastService.warning(message, undefined, title)
  }

  private collectLoadedBreakRefs(): CommentaryEntityRef[] {
    const refs: CommentaryEntityRef[] = []
    this.gridApi?.api.forEachNode(node => {
      const id = (node.data as Break)?.identifier
      if (id != null) refs.push({ type: 'break', id })
    })
    return refs
  }

  private collectSelectedBreakRefs(): CommentaryEntityRef[] {
    return (this.gridApi?.api.getSelectedNodes() ?? [])
      .map(node => (node.data as Break)?.identifier)
      .filter((id): id is string => id != null)
      .map(id => ({ type: 'break' as const, id }))
  }

  ngOnDestroy(): void {
    this.unregisterHotkeys()
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()
  }

  processBreakChange(value: Break[]) {
    if (!value) {
      return
    }

    this.gridApi?.api.applyTransaction({
      update: value,
    })
  }

  onSearchClicked(popover: NgbPopover): void {
    this.isDownloadSubmitted
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(shouldClose => {
        if (shouldClose) {
          popover.close()
        }
      })
  }

  onSearchCurrentSession() {
    this.currentSessionSearchClicked.emit()
  }

  onRefreshGrid() {
    this.refreshAfterJobCompleted.emit()
  }

  private async getAvailableRestrictedColumns(): Promise<
    BreakAvailableRestrictedColumns[]
  > {
    return this.breaksService
      .getAvailableRestrictedColumns()
      .pipe(
        takeUntil(this.destroySubject$),
        map(columns =>
          columns.map(column => {
            if (column.column === 'metaData') {
              return { column: 'metaData_8', isAvailable: column.isAvailable }
            } else return column
          }),
        ),
      )
      .toPromise()
  }

  private async createGridOptions(): Promise<GridOptions> {
    return {
      ...heliosClientSideRowConfig,
      components: {
        [commentCountRendererKey]: CommentCountCellRenderer,
      },
      defaultColDef: {
        ...heliosClientSideRowConfig.defaultColDef,
        filterParams: {
          defaultToNothingSelected: false,
        },
        cellStyle: (params: CellClassParams) =>
          this.highlightService.getCellStyle(
            params?.node?.id ?? '',
            params?.colDef?.field ?? '',
          ) as CellStyle,
      },
      getRowClass: params => [
        this.highlightService.getRowClass(params?.node?.id ?? ''),
      ],
      sideBar: generateHeliosAgGridSidebarFromScratch([
        createPanelSettingsToolPanel({
          controls: [
            {
              label: 'Clear Filters on Load',
              formControl: this.clearOnAccountLoad,
            },
          ],
        }),
        ...heliosDefaultToolPanels,
        createFilterGroupsToolPanel<unknown>(this.filterGroupsToolPanelParams),
      ]),
      statusBar: multiBreaksStatusBarPanel,
      onRowDoubleClicked: event => this.onDoubleClick(event),
      groupDefaultExpanded: 1,
      getRowNodeId: data => data.identifier,
      immutableData: true,
      groupDisplayType: 'groupRows',
      defaultGroupOrderComparator: (nodeA, nodeB) => {
        const orderA = getGroupCategoryPriority(nodeA)
        const orderB = getGroupCategoryPriority(nodeB)
        return orderA - orderB
      },
      ...heliosMultiRowSelectionConfig,
      getContextMenuItems: params => {
        const baseItems = this.contextMenuService.getContextMenu(
          params,
          !!this.isInCurrentSession,
          !!this.hasSameUltimateParent,
        )
        if (params.node?.group || !params.node?.data) return baseItems
        return [
          ...baseItems,
          ...getCommentaryContextMenuItems(
            this.store,
            this.hasAccessCommentary,
            breakCommentary,
            params.node.data,
          ),
        ]
      },
      isGroupOpenByDefault: () => true,
      processCellForClipboard: (params: ProcessCellForExportParams) => {
        if (typeof params.value === 'object') {
          // Handle Priority column object
          if (
            params.column.getColId() === 'priority' &&
            params.value?.priority
          ) {
            return params.value.priority
          }
          return JSON.stringify(params.value)
        }
        return params.value
      },
      columnDefs: await this.createBaseColumnDefs(),
      icons: {
        panelSettings: generateMdiIcon('mdi-cog'),
      },
      columnTypes: { ...breakColumnTypes },
      rowData: this.data,
      onGridReady: params => this.onGridReady(params),
      onSelectionChanged: params => this.onSelectionChanged(params),
      onFilterChanged: () => this.updateFilterCountAndEnableButton(),
      isExternalFilterPresent: () => true,
      doesExternalFilterPass: (node: RowNode) => this.testContainsInNode(node),
    }
  }

  /**
   * @internal
   */
  async createBaseColumnDefs(): Promise<ColDef[]> {
    const baseColDefs: ColDef[] = [
      ...(await getCommentaryColumnIfAccessible(
        this.store,
        breakCommentary,
        () => this.hasAccessCommentary,
        this.commentaryCountsService,
        breakCounts,
      )),
      {
        field: field('identifier'),
        headerName: 'Issue ID',
        hide: true,
        sortable: false,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: [
            {
              displayKey: 'in',
              displayName: 'In',
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              predicate: ([filterValue]: any, cellValue: string | null) =>
                filterValue.includes(cellValue),
            },
          ],
        } as ITextFilterParams,
        width: heliosDefaultColWidth.Id,
      },
      {
        field: field('categoryName'),
        headerName: 'Category Name',
        width: 120,
        rowGroup: true,
      },
      {
        field: field('cwanIdentifier'),
        headerName: 'Asset Identifier',
        initialHide: false,
        sortable: true,
        width: 120,
      },
      {
        field: field('categoryPriority'),
        headerName: 'Category Priority',
        initialHide: true,
        hide: true,
        sortable: true,
      },
      {
        field: field('displayName'),
        headerName: 'Display Name',
        width: 135,
      },
      {
        field: field('acctId'),
        headerName: 'Account ID',
        type: NfColumnTypes.Identifier,
        width: 120,
      },
      {
        ...HsBreakPriorityColDef,
        field: field('priority'),
        headerName: 'Priority',
        width: 20,
        sortable: true,
        sort: 'asc',
        sortIndex: 0,
        valueGetter: params => {
          if (!params.data) return null
          return {
            priority: params.data.priority,
            restrictedAction: params.data.restrictedAction,
            statusName: params.data.statusName,
            unverified: params.data.metaData?.Unverified,
          }
        },
        keyCreator: params => {
          // Extract priority for grouping
          return params.value?.priority ?? params.data?.priority
        },
        filterValueGetter: params => {
          return params.data?.priority
        },
        comparator: (valueA, valueB) => {
          const priorityA = valueA?.priority
          const priorityB = valueB?.priority
          if (priorityA === priorityB) return 0
          const orderedPriorityArray = ['HIGH', 'MEDIUM']
          return orderedPriorityArray.indexOf(priorityA) >
            orderedPriorityArray.indexOf(priorityB)
            ? 1
            : -1
        },
        filterParams: {
          ...HsBreakPriorityColDef.filterParams,
          values: undefined,
        },
      },
      {
        ...HsBreakStatusColDef,
        field: field('statusName'),
        headerName: 'Status',
        width: 20,
        sortable: true,
        filterParams: {
          ...HsBreakStatusColDef.filterParams,
          values: undefined,
        },
      },
      {
        field: field('securityIdentifier'),
        headerName: 'Asset ID',
        width: 83,
        sortable: true,
        filter: 'text',
        filterParams: {
          filterOptions: TextFilterOptionsWithContainsIn,
          buttons: ['apply', 'reset'],
          closeOnApply: true,
        },
      },
      {
        field: field('startDate'),
        headerName: 'Start Date',
        type: NfColumnTypes.Date,
        width: 73,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        tooltipValueGetter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
      },
      {
        field: field('createdDateTime'),
        headerName: 'Created Date',
        type: NfColumnTypes.Date,
        width: 186,
        valueFormatter: params => {
          return formatDates(
            params.value?.split('T')[0],
            'yyyy-MM-dd',
            'MM/dd/yy',
          )
        },
        tooltipValueGetter: params => {
          return params.value
        },
      },
      {
        field: field('endDate'),
        headerName: 'End Date',
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        tooltipValueGetter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
      },
      {
        field: field('tranDate'),
        headerName: 'Tran Date',
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        tooltipValueGetter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
      },
      {
        field: field('expirationDate'),
        headerName: 'Expiration Date',
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        tooltipValueGetter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
      },
      {
        colId: 'metaData',
        field: field('metaData'),
        headerName: 'Compact Values',
        sortable: true,
        enableRowGroup: false,
        valueGetter: combinedMetaDataValueGetter,
        tooltipValueGetter: params => formatTooltip(params.data.metaData),
        filter: TextFilter,
      },
      generateMetaDataColumnForNumber('metaData_1', 'CWAN Value', 'number'),
      {
        colId: 'metaData_2',
        field: field('metaData'),
        headerName: 'External Value',
        valueFormatter: params =>
          params.data.isHplAccount
            ? formatNumber(params, 4)
            : formatNumber(params, 2),
        hide: true,
        valueGetter: params => params.data?.metaData?.['Client Value'],
        filter: NumberFilter,
      },
      generateMetaDataColumnForNumber(
        'metaData_3',
        'Predicted Value',
        'number',
      ),
      {
        colId: 'metaData_4',
        field: field('metaData'),
        headerName: 'Difference',
        valueFormatter: params =>
          params.data.isHplAccount
            ? formatNumber(params, 4)
            : formatNumber(params, 2),
        hide: true,
        filter: NumberFilter,
        valueGetter: differenceValueGetter,
      },
      generateMetaDataColumn('metaData_5', 'Transaction ID', 'identifier'),
      generateMetaDataColumnForNumber('metaData_6', 'Day Difference', 'number'),
      generateMetaDataColumn('metaData_7', 'Tran Type'),
      {
        field: field('sourceName'),
        headerName: 'Source',
        hide: true,
      },
      {
        field: field('securityCusip'),
        headerName: 'Sec Cusip',
        hide: true,
      },
      {
        field: field('securityIsin'),
        headerName: 'Sec Isin',
        hide: true,
      },
      {
        field: field('securityFigi'),
        headerName: 'Sec Figi',
        hide: true,
      },
      {
        field: field('securitySedol'),
        headerName: 'Sec Sedol',
        hide: true,
      },
      {
        field: field('issueGroup'),
        headerName: 'Issue Group',
        hide: true,
      },
      {
        field: field('securityId'),
        headerName: 'Security ID',
        valueGetter: (params: ValueGetterParams) => {
          if (params.data == null) return ''
          return isNullOrEmpty(params.data.securityId)
            ? 'EMPTY'
            : params.data.securityId
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return params.data?.securityId ?? ''
        },
        filterValueGetter: params => {
          if (params.data == null) return ''
          return isNullOrEmpty(params.data.securityId)
            ? '(Blanks)'
            : params.data.securityId
        },
      },
      {
        field: field('securityType'),
        headerName: 'Security Type',
        hide: true,
        width: 80,
      },
      {
        field: field('securityName'),
        headerName: 'Security Name',
        hide: true,
        // PVAL-26384: filter-only blank bucketing. `null`, `undefined` and ''
        // all collapse to the single `(Blanks)` entry so selecting it filters
        // down to the blank rows. Cell rendering and the Excel export payload
        // are untouched (no valueGetter / valueFormatter override here).
        filterValueGetter: (params: ValueGetterParams) =>
          blanksCollapsingFilterValue(params.data?.securityName),
      },
      {
        field: field('message'),
        headerName: 'Message',
        width: 70,
      },
      {
        field: 'note',
        headerName: 'Note',
        enableRowGroup: false,
        onCellDoubleClicked: event =>
          this.editNote.emit([event.node.data as Break]),
        valueFormatter: params => formatBreakNote(params.value, true),
        sortable: true,
        filter: 'agTextColumnFilter',
      },
      {
        field: field('dataSourceTypes'),
        headerName: 'Data Source Types',
        width: 120,
      },
      {
        field: field('dataSourceName'),
        headerName: 'Data Source Name',
        width: 150,
        sortable: true,
        filter: 'agTextColumnFilter',
        enableRowGroup: true,
        valueFormatter: params => params.value ?? '',
      },
      {
        field: field('typeName'),
        headerName: 'Type Name',
      },
      {
        field: field('reconInsightIssueId'),
        headerName: 'Recon Insight Issue ID',
      },
      {
        field: field('actionCode'),
        headerName: 'Action Code',
        width: 120,
        hide: true,
      },
      {
        field: field('descriptionCode'),
        headerName: 'Description Code',
        width: 120,
        hide: true,
      },
      {
        field: field('reconInsightIssueStatus'),
        headerName: 'Recon Insight Issue Status',
      },
      {
        colId: 'metaData_8',
        field: field('metaData'),
        headerName: 'Meta Data',
        valueGetter: issueMetaDataValueGetter,
        tooltipValueGetter: params =>
          formatMetaDataTooltip(params.data.metaData),
        filter: TextFilter,
      },
      {
        field: field('ultimateParentClientId'),
        headerName: 'Ult Parent Client ID',
      },
      {
        field: field('restrictedAction'),
        headerName: 'Approval Required',
      },
      generateMetaDataColumn('metaData_9', 'Basis ID', 'identifier'),
      generateMetaDataColumn('metaData_10', 'Left Source'),
      generateMetaDataColumn('metaData_11', 'Left Value', 'number'),
      generateMetaDataColumn('metaData_12', 'Right Source'),
      generateMetaDataColumn('metaData_13', 'Right Value', 'number'),
      {
        // PVAL-26384: Type Label needs two filter-only overrides. They are
        // applied HERE, spread over the shared generator, so the other 11
        // metaData columns stay structurally unaffected.
        ...generateMetaDataColumn('metaData_14', 'Type Label'),
        // 1. Blank bucketing: `null`, `undefined`, '' and an absent
        //    'Type Label' key in metaData all collapse to one `(Blanks)` entry.
        filterValueGetter: (params: ValueGetterParams) =>
          blanksCollapsingFilterValue(
            (params.data?.metaData ?? {})['Type Label'],
          ),
        filterParams: {
          // A per-column `filterParams` REPLACES `defaultColDef.filterParams`
          // wholesale rather than merging with it, and the only key it carries
          // is `defaultToNothingSelected: false`. Restate that explicitly so
          // this column still opens with every value selected, identically to
          // the other filterable columns in this grid.
          defaultToNothingSelected: false,
          // 2. Filter-list display: the set filter formats its list entries
          //    with no row data attached, and the colDef valueFormatter
          //    (createMetaDataValueFormatter) reads metadata off params.data —
          //    so every entry would render empty. Format the filter value
          //    itself instead. Only filterParams is overridden, so the cell
          //    valueFormatter (and therefore the export payload) is unchanged.
          valueFormatter: (params: ValueFormatterParams) =>
            params.value == null ? '' : String(params.value),
        },
      },
      {
        field: field('accountName'),
        headerName: 'Account Name',
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: { filterOptions: TextFilterOptionsWithContainsIn },
      },
      {
        field: field('custodyAccountNumber'),
        headerName: 'Custody Account ID',
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: { filterOptions: TextFilterOptionsWithContainsIn },
      },
      {
        colId: BREAK_CODES_COL_ID,
        headerName: 'Break Codes',
        enableRowGroup: false,
        sortable: true,
        filter: 'agTextColumnFilter',
        valueGetter: (params: ValueGetterParams) => {
          if (!params.data) return ''
          const desc = params.data.descriptionCode ?? ''
          const action = params.data.actionCode ?? ''
          if (!desc && !action) return ''
          const note = formatBreakNote(params.data.note, true)
          return [desc, action, note].filter(Boolean).join(' | ')
        },
        tooltipValueGetter: params => {
          if (!params.data) return ''
          const desc = params.data.descriptionCode ?? ''
          const action = params.data.actionCode ?? ''
          if (!desc && !action) return ''
          const note = formatBreakNote(params.data.note, false)
          return [desc, action, note].filter(Boolean).join('\n')
        },
      },
    ]

    const savedViewExists = await this.store
      .pipe(
        selectSavedViewExistsForGridKey(BreaksMainGridKey),
        takeUntil(this.destroySubject$),
        take(1),
      )
      .toPromise()

    if (!savedViewExists) {
      return baseColDefs
    }

    const savedColumnState = await this.store
      .pipe(
        takeUntil(this.destroySubject$),
        selectActiveSavedViewForGrid(BreaksMainGridKey),
        take(1),
        map(savedView => savedView.savedView.columnState),
      )
      .toPromise()

    let colDefs = mergeBaseColDefsWithColumnState(baseColDefs, savedColumnState)

    const availableColumns = await this.availableRestrictedColumns
    if (availableColumns) {
      const columnsToHide = withDerivedRestrictedColumns(
        availableColumns
          .filter(column => !column.isAvailable)
          .map(column => column.column),
      )

      colDefs = colDefs.map(colDef => {
        if (colDef.colId && columnsToHide.includes(colDef.colId)) {
          return {
            ...colDef,
            hide: true,
            suppressMoveable: true,
            suppressMenu: true,
            suppressColumnsToolPanel: true,
          }
        }
        return colDef
      })
    }

    return colDefs
  }

  testContainsInNode(node: RowNode): boolean {
    const splittedText: string[] = this.searchText.value
      ? this.searchText.value.split(',')
      : []
    if (splittedText.length == 0) return true
    return splittedText.some(splittedString =>
      JSON.stringify(Object.values(node.data))
        .toLowerCase()
        .includes(splittedString.toLowerCase()),
    )
  }

  /**
   * Double clicking should toggle the row group or dispatch the event if it's
   * a break row
   *
   * @internal
   */
  onDoubleClick(event: RowDoubleClickedEvent) {
    const accountId = event.data.acctId as number
    const breakInCurrentSession = this.isAccountInFocusedSession(accountId)

    if (!breakInCurrentSession) {
      this.handleDestroySubjectOnDoubleClick()

      this.loadAccount(accountId)

      this.store
        .select(selectActiveSession)
        .pipe(
          takeUntil(this.destroySubjectOnDoubleClick$),
          filter(
            session =>
              !!session &&
              (session.loadedAccountIds.includes(accountId) ||
                (session.loadedAccounts ?? []).some(a =>
                  a.simpleAccountIds.includes(accountId),
                )),
          ),
          take(1),
          switchMap(() =>
            this.store
              .select(selectAllSessionCacheStatus)
              .pipe(distinctUntilChanged()),
          ),
        )
        .subscribe(status => {
          this.handleShowDetails(this.validateShowDetails(event), status)
        })
    }

    // Handle group double clicked
    if (!didHandleRowGroupDoubleClick(event) && breakInCurrentSession) {
      this.showDetails.next([event.data as Break])
    }
  }

  validateShowDetails(event: RowDoubleClickedEvent) {
    let data = event.data
    const accountId = event.data.acctId
    if (this.hasAccountId && !this.isAccountInFocusedSession(accountId)) {
      data = undefined
    }
    return data
  }

  private isAccountInFocusedSession(accountId: number): boolean {
    return (
      !!this.hasAccountId &&
      (this.currentSessionAccountIds.includes(accountId) ||
        this.sessionSimpleAccountIds.includes(accountId))
    )
  }

  /*@internal*/
  loadAccount(accountId: number) {
    this.onLoadAccount.emit({
      accountIds: [accountId],
    })
  }

  /** @internal */
  handleShowDetails(
    issue: Break,
    status: Record<SessionCacheName, SessionCacheStatus>,
  ) {
    if (!issue) return

    switch (issue.categoryName) {
      case BreakCategory.CORPORATE_ACTIONS:
        if (status.CwTransactions === 'Started') {
          this.showDetails.next([issue])
        }
        break
      case BreakCategory.PRICE_AND_ANALYTICS:
        this.showDetails.next([issue])
        break
      case BreakCategory.DISTRIBUTION:
        if (
          status.CwTransactions === 'Done' &&
          status.ExtTransactions === 'Done' &&
          status.Positions === 'Done'
        ) {
          this.showDetails.next([issue])
        }
        break
      case BreakCategory.POSITION:
        if (status.Positions === 'Done') {
          this.showDetails.next([issue])
        }
        break
      case BreakCategory.TRANSACTION_BREAKS:
      case BreakCategory.TRANSACTION_DATA_INTEGRITY:
        if (
          status.CwTransactions === 'Done' &&
          status.ExtTransactions === 'Done'
        ) {
          this.showDetails.next([issue])
        }
        break
    }
  }

  handleDestroySubjectOnDoubleClick() {
    this.destroySubjectOnDoubleClick$.next()
    this.destroySubjectOnDoubleClick$.complete()

    this.destroySubjectOnDoubleClick$ = new Subject<void>()
  }

  private async updateAvailableColumns(columnApi: ColumnApi): Promise<void> {
    const columns = await this.availableRestrictedColumns
    const columnsToHide = withDerivedRestrictedColumns(
      columns
        .filter(column => !column.isAvailable)
        .map(column => column.column),
    )

    const updatedColumns = columnsToHide.map(column => {
      return {
        colId: column,
        hide: true,
      }
    })

    columnApi.applyColumnState({
      state: updatedColumns,
      applyOrder: true,
    })
  }

  /** @internal */
  async onGridReady(params: GridReadyEvent): Promise<void> {
    this.gridApi = params

    wireCommentaryCounts<Break>({
      changes$: this.commentaryCountsService.changes$,
      getApi: () => params.api,
      hasAccessForLoad: () => this.hasAccessCommentary,
      destroy$: this.destroySubject$,
      accountIdOf: row => row?.acctId,
      loadCounts: ids =>
        this.commentaryCountsService.ensureEntityCountsLoaded(ids),
      live: { accounts: this.commentaryLiveAccounts, surface: 'entity' },
    })
    refilterOnCommentCountChanges<Break>({
      getApi: () => params.api,
      destroy$: this.destroySubject$,
      countsService: this.commentaryCountsService,
      commentary: breakCommentary,
      countDimension: breakCounts,
    })

    this.filterModalService.registerGrid(
      this.gridApi?.api,
      MultiBreaksKey,
      BreaksMainGridKey,
      [COMMENTARY_COLUMN_ID],
    )
    enableHeliosMultiRowDragSelection(this.gridApi?.api)
    enableHeliosCopyBehavior(this.gridApi?.api)

    const agGridStateSaved = await this.savedViewService.manageSavedView(
      params,
      BreaksMainGridKey,
      this.destroySubject$,
      true,
      this.getCurrentPanelSettingsObject(),
    )

    this.applyGridConfig(agGridStateSaved)
    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(
          params.api,
          DensityLevel.Compact === level,
        ),
      )
    await this.updateAvailableColumns(params.columnApi)
  }
  getCurrentPanelSettingsObject() {
    const panelSettings: { [key: string]: unknown } = {}
    panelSettings['clearFiltersOnAccountLoad'] = this.clearOnAccountLoad.value
    panelSettings['searchFilters'] = this.clearOnAccountLoad.value
      ? undefined
      : this.popOverConfigGrid
    return panelSettings
  }

  exportBreaksToExcel() {
    const colIdHeaderNameMap: { [key: string]: string } = {}
    const metaDataColumnsHeaderMapping: { [headerName: string]: string } = {
      Difference: 'Difference',
      'CWAN Value': 'CWAN Value',
      'Predicted Value': 'Predicted Value',
      'Day Difference': 'Day Difference',
      'External Value': 'Client Value',
      'Compact Values': 'Compact Value',
      'Transaction ID': 'Transaction Id',
      'Tran Type': 'Tran Type',
      'Meta Data': 'metaData',
      'Comparison Field': 'Left Source',
      'Comparison Value': 'Left Value',
      'Base Field': 'Right Source',
      'Base Value': 'Right Value',
      'Basis ID': 'Basis Id',
      'Type Label': 'Type Label',
    }

    this.gridApi?.columnApi.getAllDisplayedColumns().forEach(col => {
      const colField = col.getColId()
      const headerName = col.getUserProvidedColDef()?.headerName
      if (
        !headerName ||
        colField === 'ultimateParentClientId' ||
        colField === COMMENTARY_COLUMN_ID ||
        // PVAL-26115: `breakCodes` is a UI-only column composed by a
        // valueGetter — there is no `breakCodes` field on the Break model, and
        // the export endpoint 400s on an unknown field. Same reason
        // COMMENTARY_COLUMN_ID is skipped; see
        // libs/comments/src/lib/columns/get-commentary-column.ts.
        colField === BREAK_CODES_COL_ID
      )
        return
      colIdHeaderNameMap[headerName] =
        metaDataColumnsHeaderMapping[headerName] || colField
    })

    this.exportToExcel.emit(colIdHeaderNameMap)
  }

  onSelectionChanged(params: SelectionChangedEvent) {
    this._breaksSelection = params.api
      .getSelectedNodes()
      .filter(node => !node.group)
      .map(node => node.data)

    // check if selected breaks have the same ultimate parent id for 'bulk edit' menu
    const ultParents = this._breaksSelection.map(x => x.ultimateParentClientId)
    const ultParentSet = new Set(ultParents)
    this.hasSameUltimateParent = ultParentSet.size === 1

    if (this._breaksSelection.length === 0) return
    if (
      this._breaksSelection[0].securityType == 'CASH' &&
      (this._breaksSelection[0].issueGroup == 'INCORRECT_POSITION' ||
        this._breaksSelection[0].issueGroup == 'MISSING_EXTERNAL_POSITION')
    ) {
      const ultClientData = getUltClientData(
        this.ultClientData,
        this._breaksSelection[0].acctId,
      )
      this.updateUltClientData.emit({
        ultClientName: ultClientData[0],
        ultClientId: ultClientData[1],
      })
    }
  }

  launchFiltersModal() {
    this.filterModalService.launchFiltersModal()
  }

  clearFilters() {
    this.gridApi?.api.setFilterModel(null)
    this.resetPopOverFilter.emit()
  }

  onHoldSelectedBreaks(days: number) {
    if (this._breaksSelection.length === 0) {
      return
    }
    this.hold.emit({ breaks: this._breaksSelection, days })
  }
  onAcceptSelectedBreaks() {
    if (this._breaksSelection.length === 0) {
      return
    }
    this.accept.emit(this._breaksSelection)
  }

  /**
   * alt+c. Reads the LIVE grid selection, not the cached `_breaksSelection`: an
   * `applyTransaction({ update })` from a break edit swaps `node.data` for a new object
   * WITHOUT firing selection-changed, so the cache would hand back pre-edit codes — and
   * copy puts them straight on the clipboard behind a "copied" toast. (hold/accept hand
   * the same cached objects to their dialogs; pre-existing, tracked separately.)
   *
   * A triple only has meaning from exactly one break, so a multi-row selection reports
   * why nothing happened — silence would leave the PREVIOUS break on the clipboard and
   * a later paste would apply the wrong codes. An empty selection is a stray keypress.
   */
  onCopySelectedBreakCodes() {
    const selected = (this.gridApi?.api.getSelectedNodes() ?? [])
      .filter(node => !node.group)
      .map(node => node.data as Break)

    if (selected.length !== 1) {
      if (selected.length > 0) {
        // Same wording as the context menu's disabled-item tooltip.
        this.toastService.error('Select a single break to copy its break codes')
      }
      return
    }
    this.breakCodeClipboard.copy(selected[0])
    this.toastService.success(BREAK_CODES_COPIED)
  }

  getRegisteredHotkeyCount(): number {
    return this.hotkeyDestroyStack.length
  }

  private registerHotkeys() {
    this.hotkeyDestroyStack.push(
      this.hotkeysService.bind('hold-selected-breaks-1', () => {
        this.onHoldSelectedBreaks(1)
      }),
      this.hotkeysService.bind('hold-selected-breaks-2', () => {
        this.onHoldSelectedBreaks(2)
      }),
      this.hotkeysService.bind('hold-selected-breaks-3', () => {
        this.onHoldSelectedBreaks(3)
      }),
      this.hotkeysService.bind('hold-selected-breaks-4', () => {
        this.onHoldSelectedBreaks(4)
      }),
      this.hotkeysService.bind('hold-selected-breaks-5', () => {
        this.onHoldSelectedBreaks(5)
      }),
      this.hotkeysService.bind('accept-selected-break', () => {
        this.onAcceptSelectedBreaks()
      }),
      this.hotkeysService.bind('copy-break-codes', () => {
        this.onCopySelectedBreakCodes()
      }),
    )
  }

  private unregisterHotkeys() {
    this.hotkeyDestroyStack.forEach(hotkeyDestroyFn => hotkeyDestroyFn())
    this.hotkeyDestroyStack.length = 0
  }

  private onFilterClearingStatusChanged() {
    this.updateGridConfig()
  }

  updateGridConfig() {
    const panelSettingsChangedEvent = new CustomEvent('panelSettingsChanged', {
      detail: {
        api: this.gridApi,
        panelSettings: this.getCurrentPanelSettingsObject(),
      } as WorkspaceUpdateEventObject,
    })
    this.gridApi?.api.dispatchEvent(panelSettingsChangedEvent)
  }

  setPopOverOptions(popConfig: FindBreaksForm | undefined) {
    if (!popConfig) {
      return
    }
    this.popOverConfigGrid = popConfig
    this.updateFilterCountAndEnableButton()
  }

  private applyGridConfig(agGridStateSaved: AgGridState) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const panelSettings = agGridStateSaved.panelSettings as any

    if (panelSettings) {
      if ('clearFiltersOnAccountLoad' in panelSettings)
        this.clearOnAccountLoad.patchValue(
          Boolean(panelSettings.clearFiltersOnAccountLoad),
          { emitEvent: false },
        )
      if (panelSettings.searchFilters) {
        this.setPopOverOptions(panelSettings.searchFilters)
        this.initialSearchFilters.emit(panelSettings.searchFilters)
      }
    }
  }

  updateFilterCountAndEnableButton() {
    if (!this.gridApi) {
      return
    }
    const filterModels = this.gridApi?.api.getFilterModel() ?? {}
    const activeFilters = Object.keys(filterModels)
    const isBreakStatusDefault =
      this.popOverConfigGrid.breakStatus.length === 1 &&
      this.popOverConfigGrid.breakStatus[0] == 'FOUND'

    const popoverFilterCount =
      Number(!isBreakStatusDefault) +
      Number(this.popOverConfigGrid.breakTypes.length !== 0) +
      Number(this.popOverConfigGrid.breakPriority.length !== 0) +
      Number(this.popOverConfigGrid.searchByIds.length !== 0) +
      Number(this.popOverConfigGrid.breakCategories.length !== 0)

    this.clearFiltersEnabled =
      activeFilters.length > 0 ||
      !isBreakStatusDefault ||
      popoverFilterCount > 0
    this.filterCount =
      (countFilters(this.gridApi?.api, null) ?? 0) + popoverFilterCount
  }
}
